<template>
  <div @keyup.enter="handleEnterKey">
    <button class="flex-y-center gap-2 group" @click="$emit('back')">
      <i
        class="icon-chevron-left text-lg md:text-2xl block font-thin text-dark transition group-hover:-translate-x-1"
      />
      <span
        class="font-interDisplay text-2xl md:text-[32px] leading-130 font-bold text-dark"
      >
        {{ type === "create" ? $t("creating_password") : $t("recovery") }}
      </span>
    </button>
    <p class="text-sm md:text-base leading-130 font-normal text-gray-0/60 mt-1">
      {{ $t("create_strong_password") }}
    </p>
    <div class="space-y-4 mt-6">
      <SInput
        v-model.trim="form.values.password"
        :error="form.$v.value.password.$error"
        :placeholder="$t('create_new_password')"
        :type="isPassword1 ? 'password' : 'text'"
        prefix-class="text-2xl text-gray-450 font-semibold flex items-center mr-3 leading-130"
        :id="form.values.password"
      >
        <template #prefix>
          <i class="icon-password" />
        </template>
        <template #suffix>
          <SEyeToggle
            class="translate-y-0.5"
            :type-password="!isPassword1"
            @click="isPassword1 = !isPassword1"
            hover-color="#333"
          />
        </template>
      </SInput>

      <SInput
        v-model.trim="form.values.confirm_password"
        :error="form.$v.value.confirm_password.$error"
        :placeholder="$t('enter_again_password')"
        :type="isPassword2 ? 'password' : 'text'"
        :id="form.values.confirm_password"
        prefix-class="text-2xl text-gray-450 font-semibold flex items-center mr-3 leading-130"
      >
        <template #prefix>
          <i class="icon-password" />
        </template>
        <template #suffix>
          <SEyeToggle
            class="translate-y-0.5"
            :type-password="!isPassword2"
            @click="isPassword2 = !isPassword2"
            hover-color="#333"
          />
        </template>
      </SInput>
    </div>
    <SButton
      class="w-full mt-8"
      :text="type === 'create' ? $t('send') : $t('send')"
      @click="submit"
      :loading="loading || regLoading || logLoading"
      :disabled="form.$v.value.$invalid"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import SButton from "@/components/Common/CButton.vue";
import { useCustomToast } from "@/composables/useCustomToast";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/modules/Auth/stores";
import ApiService from "@/services/ApiService";
import { useForm } from "@/composables/useForm";
import { minLength, required } from "@vuelidate/validators";
import SEyeToggle from "@/components/Form/Input/CEyeToggle.vue";
import SInput from "@/components/Form/Input/FInput.vue";

interface Props {
  email?: string;
  regLoading?: boolean;
  logLoading?: boolean;
  type?: "create" | "reset";
}

import { usePushToDataLayer } from "@/composables/usePushToDataLayer";
const { pushToDataLayer } = usePushToDataLayer();

const props = withDefaults(defineProps<Props>(), {
  type: "reset",
});

const isPassword1 = ref(true);
const isPassword2 = ref(true);
const form = useForm(
  {
    password: "",
    confirm_password: "",
  },
  {
    password: {
      required,
      minLength: minLength(8),
    },
    confirm_password: {
      required,
      sameAs: (val: string) => val === form.values.password,
      minLength: minLength(8),
    },
  },
);

const emit = defineEmits<{
  (e: "submitPassword", value: string): void;
  (e: "submit"): void;
  (e: "back"): void;
  (e: "on-block"): void;
}>();

const { showToast } = useCustomToast();
const store = useAuthStore();
const { t } = useI18n();

const loading = ref(false);

async function submit() {
  form.$v.value.$touch();

  if (!form.$v.value.$invalid) {
    if (props?.type === "create") {
      emit("submitPassword", form.values.password);
    } else {
      changePassword();
    }
  }
}
const handleEnterKey = (event: KeyboardEvent) => {
  if (event.code === "Enter") {
    submit();
  }
};
function changePassword() {
  loading.value = true;

  const formData = new FormData();
  formData.append("password", form.values.password);

  ApiService.patch(`user/profile/`, formData)
    .then(() => {
      showToast(t("successfully_changed_password"), "success");
      localStorage.clear();
      emit("submit");
    })
    .catch((err: any) => {
      if (err?.response?.status === 429) {
        emit("on-block");
        store.blockedTime = +err?.response?.headers["retry-after"];
      }
      showToast(
        err?.response?.data?.[0]?.error?.message || t("some_error"),
        "error",
      );
    })
    .finally(() => (loading.value = false));
}

onMounted(() => {
  pushToDataLayer("pageview", {
    page: "forget_recovery",
    event_category: "page",
    event_label: "forget_recovery_page_view",
    button_text: "None",
  });
});
</script>
